import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  resetAllApiStatus,
  fetchTasksApiThunk,
  searchTasksApiThunk,
  getTaskApiThunk,
  getTaskStatsApiThunk,
  updateAssigneeApiThunk,
  auditorUpdateApiThunk,
  cancelTaskApiThunk,
  createTaskApiThunk,
  createTaskFromQuestionApiThunk,
  reopenTaskApiThunk,
} from './tasksSlice';
import { RootState } from '..';
import {
  ListTasksRequest,
  SearchTasksRequest,
  GetTaskRequest,
  GetTaskStatsRequest,
  AssigneeUpdateTaskRequest,
  AuditorUpdateTaskRequest,
  CancelTaskRequest,
  CreateTaskRequest,
  CreateTaskFromQuestionRequest,
  ReopenTaskRequest,
} from 'src/types/helios/Task';

export const useTasks = () => {
  const dispatch = useAppDispatch();

  // Selectors
  const tasksList = useAppSelector((state: RootState) => state.tasks.tasks);
  const selectedTask = useAppSelector((state: RootState) => state.tasks.selectedTask);
  const taskStats = useAppSelector((state: RootState) => state.tasks.taskStats);
  const fetchTasksApiStatus = useAppSelector(
    (state: RootState) => state.tasks.fetchTasksApiStatus,
  );
  const searchTasksApiStatus = useAppSelector(
    (state: RootState) => state.tasks.searchTasksApiStatus,
  );
  const getTaskApiStatus = useAppSelector(
    (state: RootState) => state.tasks.getTaskApiStatus,
  );
  const taskStatsApiStatus = useAppSelector(
    (state: RootState) => state.tasks.taskStatsApiStatus,
  );
  const updateAssigneeApiStatus = useAppSelector(
    (state: RootState) => state.tasks.updateAssigneeApiStatus,
  );
  const auditorUpdateApiStatus = useAppSelector(
    (state: RootState) => state.tasks.auditorUpdateApiStatus,
  );
  const cancelTaskApiStatus = useAppSelector(
    (state: RootState) => state.tasks.cancelTaskApiStatus,
  );
  const reopenTaskApiStatus = useAppSelector(
    (state: RootState) => state.tasks.reopenTaskApiStatus,
  );
  const createTaskApiStatus = useAppSelector(
    (state: RootState) => state.tasks.createTaskApiStatus,
  );
  const createTaskFromQuestionApiStatus = useAppSelector(
    (state: RootState) => state.tasks.createTaskFromQuestionApiStatus,
  );

  // Load tasks
  const loadTasks = useCallback(
    (params: ListTasksRequest) => {
      dispatch(fetchTasksApiThunk(params));
    },
    [dispatch],
  );

  // Search tasks
  const searchTasks = useCallback(
    (params: SearchTasksRequest) => {
      // Handle date range validation similar to audits
      if (
        params.dateRange?.type === 'CUSTOM' &&
        (!params.dateRange.startDate || !params.dateRange.endDate)
      ) {
        return;
      }

      // Validate start date is not after end date
      if (
        params.dateRange?.type === 'CUSTOM' &&
        params.dateRange.startDate &&
        params.dateRange.endDate &&
        new Date(params.dateRange.startDate) > new Date(params.dateRange.endDate)
      ) {
        return;
      }

      const newParams = {
        ...params,
        dateRange: params.dateRange
          ? {
              ...params.dateRange,
              startDate: params.dateRange.startDate
                ? params.dateRange.startDate + 'T00:00:00Z'
                : params.dateRange.startDate,
              endDate: params.dateRange.endDate
                ? params.dateRange.endDate + 'T00:00:00Z'
                : params.dateRange.endDate,
            }
          : params.dateRange,
      };

      dispatch(searchTasksApiThunk(newParams));
    },
    [dispatch],
  );

  // Get single task
  const getTask = useCallback(
    (params: GetTaskRequest) => {
      dispatch(getTaskApiThunk(params));
    },
    [dispatch],
  );

  // Update task assignee
  const updateAssignee = useCallback(
    (params: AssigneeUpdateTaskRequest) => {
      dispatch(updateAssigneeApiThunk(params));
    },
    [dispatch],
  );

  // Update task by auditor
  const auditorUpdate = useCallback(
    (params: AuditorUpdateTaskRequest) => {
      dispatch(auditorUpdateApiThunk(params));
    },
    [dispatch],
  );

  // Cancel task
  const cancelTask = useCallback(
    (params: CancelTaskRequest) => {
      dispatch(cancelTaskApiThunk(params));
    },
    [dispatch],
  );

  // Reopen task
  const reopenTask = useCallback(
    (params: ReopenTaskRequest) => {
      dispatch(reopenTaskApiThunk(params));
    },
    [dispatch],
  );

  // Create task
  const createTask = useCallback(
    (params: CreateTaskRequest) => {
      dispatch(createTaskApiThunk(params));
    },
    [dispatch],
  );

  // Create task from question
  const createTaskFromQuestion = useCallback(
    (params: CreateTaskFromQuestionRequest) => {
      dispatch(createTaskFromQuestionApiThunk(params));
    },
    [dispatch],
  );

  // Get task stats
  const getTaskStats = useCallback(
    (params: GetTaskStatsRequest) => {
      dispatch(getTaskStatsApiThunk(params));
    },
    [dispatch],
  );

  // Clear API status messages
  const clearAPIStatusMessage = useCallback(() => {
    dispatch(resetAllApiStatus());
  }, [dispatch]);

  return {
    // State
    tasksList,
    selectedTask,
    taskStats,

    fetchTasksApiStatus,
    searchTasksApiStatus,
    getTaskApiStatus,
    taskStatsApiStatus,
    updateAssigneeApiStatus,
    auditorUpdateApiStatus,
    cancelTaskApiStatus,
    reopenTaskApiStatus,
    createTaskApiStatus,
    createTaskFromQuestionApiStatus,
    // Actions
    loadTasks,
    searchTasks,
    getTask,
    getTaskStats,
    updateAssignee,
    auditorUpdate,
    cancelTask,
    reopenTask,
    createTask,
    createTaskFromQuestion,
    clearAPIStatusMessage,
  };
};
